import * as React from 'react';
import { MapPin } from 'lucide-react';
import { Input } from '@/components/forms/Input';
import { AddressAutocompleteInput } from '@/components/forms/AddressAutocompleteInput';
import { DepartmentSelect } from '@/components/common/DepartmentSelect';
import { establishmentAddressFromAutocomplete } from '@/components/establishments/establishment-form-utils';
import type {
  EstablishmentFormFieldValidation,
  EstablishmentFormState,
} from '@/types/establishment.types';
import type { AddressSelectionResult } from '@/types/geography.types';

/** Props for {@link EstablishmentAddressSection}. */
interface Props {
  form: EstablishmentFormState;
  onFieldChange: (field: string, value: unknown) => void;
  onBlur: (field: string) => void;
  onFormPatch: (patch: Partial<EstablishmentFormState>) => void;
  validation: EstablishmentFormFieldValidation;
}

/** Address autocomplete and location fields. */
export function EstablishmentAddressSection({
  form,
  onFieldChange,
  onBlur,
  onFormPatch,
  validation,
}: Readonly<Props>) {
  function handleAddressChange(value: string): void {
    onFieldChange('addressLine1', value);
  }
  function handleAddressBlur(): void {
    onBlur('addressLine1');
  }
  function handleAddressSelection(result: AddressSelectionResult): void {
    onFormPatch(establishmentAddressFromAutocomplete(result));
  }
  function handlePostalCodeChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('postalCode', e.target.value);
  }
  function handlePostalCodeBlur(): void {
    onBlur('postalCode');
  }
  function handleCityChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('city', e.target.value);
  }
  function handleCityBlur(): void {
    onBlur('city');
  }
  function handleCountryChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('country', e.target.value);
  }
  function handleCountryBlur(): void {
    onBlur('country');
  }
  function handleDepartmentChange(value: string): void {
    onFieldChange('departmentCode', value);
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <MapPin className="w-4 h-4" />
        Adresse
      </h4>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <AddressAutocompleteInput
          label="Adresse"
          value={form.addressLine1}
          onChange={handleAddressChange}
          onBlur={handleAddressBlur}
          onSelection={handleAddressSelection}
          placeholder="Ex: 12 rue de la République, Paris"
          size="sm"
          state={validation.getFieldState('addressLine1')}
          error={validation.getFieldError('addressLine1')}
          maxLength={255}
        />
        <Input
          label="Code postal"
          value={form.postalCode}
          onChange={handlePostalCodeChange}
          onBlur={handlePostalCodeBlur}
          placeholder="Ex: 75001"
          maxLength={20}
          inputSize="sm"
          state={validation.getFieldState('postalCode')}
          error={validation.getFieldError('postalCode')}
        />
        <Input
          label="Ville"
          value={form.city}
          onChange={handleCityChange}
          onBlur={handleCityBlur}
          placeholder="Ex: Paris"
          maxLength={100}
          inputSize="sm"
          state={validation.getFieldState('city')}
          error={validation.getFieldError('city')}
        />
        <Input
          label="Pays"
          value={form.country}
          onChange={handleCountryChange}
          onBlur={handleCountryBlur}
          placeholder="Ex: FR"
          maxLength={2}
          inputSize="sm"
          state={validation.getFieldState('country')}
          error={validation.getFieldError('country')}
        />
        <DepartmentSelect
          label="Département"
          value={form.departmentCode}
          onChange={handleDepartmentChange}
          placeholder="Sélectionner"
          inputSize="sm"
        />
      </div>
    </div>
  );
}
